<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import EmpresaDocumentsAPI from 'dashboard/api/empresaDocuments';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  empresaId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();

const documents = ref([]);
const isLoading = ref(false);
const isUploading = ref(false);
const isDragging = ref(false);
const fileInputRef = ref(null);

const formatFileSize = bytes => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const loadDocuments = async () => {
  isLoading.value = true;
  try {
    const { data } = await EmpresaDocumentsAPI.get(props.empresaId);
    documents.value = data || [];
  } catch (error) {
    documents.value = [];
  } finally {
    isLoading.value = false;
  }
};

const uploadFiles = async files => {
  if (!files.length) return;
  isUploading.value = true;
  try {
    // Upload sequentially (one attach per request) without a loop statement.
    await files.reduce(
      (chain, file) =>
        chain.then(() => EmpresaDocumentsAPI.upload(props.empresaId, file)),
      Promise.resolve()
    );
    await loadDocuments();
  } catch (error) {
    useAlert(t('EMPRESAS.DOCUMENTS.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const onFileInput = e => {
  uploadFiles(Array.from(e.target.files));
  e.target.value = '';
};

const onDrop = e => {
  e.preventDefault();
  isDragging.value = false;
  uploadFiles(Array.from(e.dataTransfer.files));
};

const onDragOver = e => {
  e.preventDefault();
  isDragging.value = true;
};

const onDragLeave = e => {
  if (e.currentTarget.contains(e.relatedTarget)) return;
  isDragging.value = false;
};

const removeDocument = async doc => {
  try {
    await EmpresaDocumentsAPI.delete(props.empresaId, doc.id);
    documents.value = documents.value.filter(d => d.id !== doc.id);
  } catch (error) {
    useAlert(t('EMPRESAS.DOCUMENTS.DELETE_ERROR'));
  }
};

watch(() => props.empresaId, loadDocuments);
onMounted(loadDocuments);
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Document list -->
    <ul v-if="documents.length" class="flex flex-col divide-y divide-n-weak/60">
      <li
        v-for="doc in documents"
        :key="doc.id"
        class="flex items-center gap-2.5 py-2 group"
      >
        <span class="shrink-0 i-lucide-file-text size-4 text-n-slate-10" />
        <a
          :href="doc.file_url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex-1 min-w-0 text-sm truncate text-n-slate-12 hover:text-n-blue-text"
        >
          {{ doc.filename }}
        </a>
        <span class="text-xs shrink-0 text-n-slate-10">
          {{ formatFileSize(doc.byte_size) }}
        </span>
        <Button
          v-tooltip.top="t('EMPRESAS.DOCUMENTS.DELETE')"
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-x"
          class="transition-opacity opacity-0 shrink-0 group-hover:opacity-100"
          @click="removeDocument(doc)"
        />
      </li>
    </ul>

    <!-- Drop zone -->
    <button
      type="button"
      class="flex flex-col items-center justify-center gap-1 px-4 py-5 text-sm text-center transition-colors border-2 border-dashed rounded-xl"
      :class="
        isDragging
          ? 'border-n-brand bg-n-alpha-1 text-n-slate-12'
          : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
      "
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
      @click="fileInputRef?.click()"
    >
      <span
        class="text-lg"
        :class="
          isUploading
            ? 'i-lucide-loader-circle animate-spin'
            : 'i-lucide-cloud-upload'
        "
      />
      <span>
        {{
          isUploading
            ? t('EMPRESAS.DOCUMENTS.UPLOADING')
            : t('EMPRESAS.DOCUMENTS.DROP_HINT')
        }}
      </span>
      <input
        ref="fileInputRef"
        type="file"
        multiple
        class="sr-only"
        @change="onFileInput"
      />
    </button>
  </div>
</template>
